import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trash2, ChevronLeft, Scale, ShoppingBag } from 'lucide-react';
import { useCompare } from '../contexts/CompareContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useCart } from '../contexts/CartContext';
import LazyImage from '../components/LazyImage';
import SEOHead from '../components/SEOHead';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

/**
 * /compare — side-by-side comparison of up to 3 scents.
 *
 * Renders a responsive grid of selected products with rows for image,
 * name, price, scent family, top/heart/base notes, longevity, projection,
 * and a quick "Add to cart" / "Remove" pair. Empty state nudges the user
 * back to /shop with instructions on how to add scents.
 */
const FIELDS = [
  { key: 'category', label: 'Category', fn: p => p.category || '—' },
  { key: 'gender', label: 'Gender', fn: p => p.gender || '—' },
  { key: 'scent_family', label: 'Scent family', fn: p => p.scent_family || '—' },
  { key: 'top_notes', label: 'Top notes', fn: p => p.top_notes || p.notes_top || '—' },
  { key: 'heart_notes', label: 'Heart notes', fn: p => p.heart_notes || p.notes_heart || '—' },
  { key: 'base_notes', label: 'Base notes', fn: p => p.base_notes || p.notes_base || '—' },
  { key: 'longevity', label: 'Longevity', fn: p => p.longevity || '6-8 hours' },
  { key: 'projection', label: 'Projection', fn: p => p.projection || 'Moderate–Strong' },
  { key: 'concentration', label: 'Concentration', fn: () => 'EDP · 30% Oil · Extrait-level' },
];

export default function ComparePage() {
  const { ids, remove, clear, max } = useCompare();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API}/products`).then(r => {
      const byId = new Map((r.data || []).map(p => [p.id, p]));
      setProducts(ids.map(id => byId.get(id)).filter(Boolean));
    }).catch(() => setProducts([])).finally(() => setLoading(false));
  }, [ids]);

  const addAll = () => {
    let added = 0;
    products.forEach(p => { addToCart(p, 1, null); added += 1; });
    if (added > 0) toast.success(`Added ${added} scent${added === 1 ? '' : 's'} to cart`);
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] py-12 sm:py-20 px-4" data-testid="compare-page">
      <SEOHead title="Compare Scents" description="Compare JS Fragrance scents side-by-side." robots="noindex,nofollow" />

      <div className="max-w-6xl mx-auto">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white mb-6 font-sans"
          data-testid="compare-back"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Shop
        </Link>

        <header className="mb-8 sm:mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-sans flex items-center gap-2">
            <Scale className="w-3.5 h-3.5" /> Side-by-side
          </p>
          <h1 className="text-3xl sm:text-4xl text-white font-light mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Compare Scents
          </h1>
          <p className="text-white/40 text-sm font-sans mt-2">
            Up to {max} scents · pick the one that fits your story.
          </p>
        </header>

        {loading ? (
          <div className="text-white/40 text-sm font-sans py-20 text-center">Loading…</div>
        ) : products.length === 0 ? (
          <div
            data-testid="compare-empty"
            className="border border-dashed border-white/15 bg-[#141414]/60 p-10 sm:p-14 text-center"
          >
            <Scale className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.2} />
            <p className="text-white text-lg font-sans mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              No scents to compare yet
            </p>
            <p className="text-white/50 text-sm font-sans mb-6 max-w-md mx-auto">
              On any product card or product page, tap the <span className="text-[#D4AF37]">⚖ Compare</span> button to add up to {max} scents — then come back here.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#F3E5AB] transition-colors"
              data-testid="compare-empty-shop-link"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> Browse Scents
            </Link>
          </div>
        ) : (
          <>
            {/* Top card row with images, names, prices, actions */}
            <div className={`grid grid-cols-1 ${products.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'} gap-4 mb-6`}>
              {products.map(p => (
                <div
                  key={p.id}
                  className="border border-white/10 bg-[#141414] p-4"
                  data-testid={`compare-card-${p.id}`}
                >
                  <Link to={`/product/${p.id}`} className="block aspect-[3/4] bg-[#0A0A0A] overflow-hidden mb-3">
                    <LazyImage src={p.image_url} alt={p.name} className="w-full h-full opacity-90 hover:opacity-100 transition-opacity" />
                  </Link>
                  <h3 className="text-white text-base font-medium leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{p.name}</h3>
                  <p className="text-[#D4AF37] text-sm font-sans mt-1">{formatPrice(p.price || p.base_price)}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => { addToCart(p, 1, null); toast.success('Added to cart'); }}
                      className="flex-1 px-3 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.18em] font-sans hover:bg-[#F3E5AB] transition-colors"
                      data-testid={`compare-add-${p.id}`}
                    >
                      Add to cart
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(p.id)}
                      title="Remove from compare"
                      className="p-2 border border-white/15 text-white/60 hover:text-red-400 hover:border-red-400/40 transition-colors"
                      data-testid={`compare-remove-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Spec table */}
            <div className="border border-white/10 overflow-x-auto" data-testid="compare-table">
              <table className="w-full text-sm font-sans border-collapse">
                <tbody>
                  {FIELDS.map((f, i) => (
                    <tr key={f.key} className={i % 2 === 0 ? 'bg-[#0F0F0F]' : 'bg-[#141414]'}>
                      <th className="text-left text-white/40 text-[11px] uppercase tracking-[0.18em] px-4 py-3 font-sans font-normal w-40 align-top">
                        {f.label}
                      </th>
                      {products.map(p => (
                        <td key={p.id} className="px-4 py-3 text-white/80 align-top">
                          {f.fn(p)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
              <button
                type="button"
                onClick={clear}
                data-testid="compare-clear"
                className="text-xs uppercase tracking-[0.2em] text-white/40 hover:text-red-400 font-sans flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Clear All
              </button>
              <button
                type="button"
                onClick={addAll}
                data-testid="compare-add-all"
                className="inline-flex items-center gap-2 bg-[#1C7D3F] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-sans hover:bg-[#2E9B50] transition-colors"
              >
                <ShoppingBag className="w-3.5 h-3.5" /> Add All to Cart
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
